/**
 * La finestra dell'assistente: testata con titolo e tre azioni, poi messaggi e
 * campo di scrittura. E' un `dialog` non modale: l'operatore puo' continuare a
 * lavorare nella pagina mentre e' aperta; a tutto schermo (mobile o espansa)
 * diventa modale e intrappola il fuoco.
 */

import marchioAstra from "../assets/astra-mark.svg";
import { useEffect, useRef, type KeyboardEvent, type ReactNode } from "react";
import { IconaChiudiWidget, IconaEspandi, IconaRestringi, IconaRiduci } from "./icone";

interface Proprieta {
  titolo: string;
  sottotitolo: string | null;
  espanso: boolean;
  mobile: boolean;
  onRiduci: () => void;
  onEspandi: () => void;
  onChiudi: () => void;
  children: ReactNode;
}

const FOCALIZZABILI =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Pannello({ titolo, sottotitolo, espanso, mobile, onRiduci, onEspandi, onChiudi, children }: Proprieta) {
  const radice = useRef<HTMLDivElement>(null);
  const modale = mobile || espanso;

  // Trappola del fuoco quando il pannello copre la pagina: Tab non deve
  // scappare dietro a un contenuto che non si vede.
  const suTasto = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab" || !modale || !radice.current) return;
    const elementi = Array.from(radice.current.querySelectorAll<HTMLElement>(FOCALIZZABILI));
    if (elementi.length === 0) return;
    const primo = elementi[0];
    const ultimo = elementi[elementi.length - 1];
    if (e.shiftKey && document.activeElement === primo) {
      e.preventDefault();
      ultimo.focus();
    } else if (!e.shiftKey && document.activeElement === ultimo) {
      e.preventDefault();
      primo.focus();
    }
  };

  useEffect(() => {
    if (!modale) return;
    const precedente = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = precedente;
    };
  }, [modale]);

  return (
    <div
      id="assistente-pannello"
      ref={radice}
      className={["assistente-pannello", espanso ? "assistente-pannello--espanso" : ""].filter(Boolean).join(" ")}
      role="dialog"
      aria-modal={modale || undefined}
      aria-label="Assistente alla trasparenza"
      onKeyDown={suTasto}
    >
      <header className="assistente-pannello__testa">
        <div className="assistente-pannello__identita">
          <span className="assistente-pannello__logo" aria-hidden="true">
            <img src={marchioAstra} alt="" />
          </span>
          <div>
            <div className="assistente-pannello__titolo">{titolo}</div>
            {sottotitolo ? <div className="assistente-pannello__sottotitolo">{sottotitolo}</div> : null}
          </div>
        </div>
        <div className="assistente-pannello__azioni">
          <button type="button" onClick={onRiduci} aria-label="Riduci a icona" title="Riduci a icona">
            <IconaRiduci dimensione={18} />
          </button>
          {!mobile ? (
            <button
              type="button"
              onClick={onEspandi}
              aria-label={espanso ? "Torna alla dimensione normale" : "Espandi"}
              title={espanso ? "Torna alla dimensione normale" : "Espandi"}
            >
              {espanso ? <IconaRestringi dimensione={18} /> : <IconaEspandi dimensione={18} />}
            </button>
          ) : null}
          <button type="button" onClick={onChiudi} aria-label="Chiudi e azzera la conversazione" title="Chiudi">
            <IconaChiudiWidget dimensione={18} />
          </button>
        </div>
      </header>
      {children}
    </div>
  );
}
